/**
 * Authentication utilities for API requests
 *
 * Provides runtime authentication header generation for Basic auth.
 * Credentials are never hardcoded - they must be provided at runtime
 * via login flow, environment config, or localStorage.
 */

export interface AuthCredentials {
  username: string;
  password: string;
}

/**
 * Storage key for auth token in localStorage
 */
const AUTH_STORAGE_KEY = 'adminUserInfo';

/**
 * Get stored authentication token from localStorage
 * Returns the Authorization header value if available
 */
export function getStoredAuthToken(): string | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const { token } = JSON.parse(stored);
      return token || null;
    }
  } catch (error) {
    console.error('Failed to parse auth token from localStorage:', error);
  }
  return null;
}

/**
 * Create Basic auth header value from credentials
 * @param credentials - Username and password
 * @returns Base64 encoded Basic auth header value
 */
export function createBasicAuthHeader(credentials: AuthCredentials): string {
  const encoded = btoa(`${credentials.username}:${credentials.password}`);
  return `Basic ${encoded}`;
}

/**
 * Store auth token in localStorage
 * @param token - The authorization header value (e.g., "Basic xxx" or "Token xxx")
 */
export function storeAuthToken(token: string): void {
  const existing = localStorage.getItem(AUTH_STORAGE_KEY);
  let data: Record<string, unknown> = {};

  try {
    if (existing) {
      data = JSON.parse(existing);
    }
  } catch {
    // Ignore parse errors
  }

  data.token = token;
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(data));
}

/**
 * Clear stored auth token
 */
export function clearAuthToken(): void {
  try {
    const existing = localStorage.getItem(AUTH_STORAGE_KEY);
    if (existing) {
      const data = JSON.parse(existing);
      delete data.token;
      if (Object.keys(data).length > 0) {
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(data));
      } else {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      }
    }
  } catch {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }
}

/**
 * Get Authorization header for API requests
 * Returns the header object if auth is available, empty object otherwise
 */
export function getAuthHeader(): Record<string, string> {
  const token = getStoredAuthToken();
  if (token) {
    return { Authorization: token };
  }
  return {};
}

/**
 * Check if user is currently authenticated
 */
export function isAuthenticated(): boolean {
  return getStoredAuthToken() !== null;
}
